import React, { createContext, useEffect, useState } from 'react'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { LOCAL_STORAGE_KEYS } from '@/lib/storage-keys'

import type { PromotionCode as PaymentServicePromotionCode } from '@/clients/payment-service'

export type PromotionCode = Pick<PaymentServicePromotionCode, 'code' | 'id'>

export interface BasketItem {
  priceLookupKey: string
  quantity?: number
}

export interface BasketData {
  items: BasketItem[]
  promotionCode: PromotionCode | null
}

interface BasketContextProps {
  basketData: BasketData
  setBasketData: React.Dispatch<React.SetStateAction<BasketData>>
  isBasketInitialised: boolean
}

export const BasketContext = createContext<BasketContextProps | undefined>(undefined)

interface BasketProviderProps {
  children: React.ReactNode
}

export const BasketProvider: React.FC<BasketProviderProps> = ({ children }) => {
  const router = useTrackedRouter()
  const [basketData, setBasketData] = useState<BasketData>({
    items: [],
    promotionCode: null,
  })
  const [isBasketInitialised, setIsBasketInitialised] = useState(false)

  useEffect(() => {
    setIsBasketInitialised(false)
    if (typeof window !== 'undefined') {
      try {
        const storedBasket = localStorage.getItem(LOCAL_STORAGE_KEYS.BASKET)
        if (storedBasket) {
          const parsed = JSON.parse(storedBasket)
          // Clear basket if it uses the old { priceLookupKeys: string[] } format, which is incompatible with the current { items: BasketItem[] } schema
          if (Array.isArray(parsed.priceLookupKeys) && !parsed.items) {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.BASKET)
          } else {
            setBasketData(parsed)
          }
        }
      } catch (error) {
        console.error('Error loading basket data from localStorage:', error)
      }
    }
    setIsBasketInitialised(true)
  }, [])

  // Sync promoCodeId from URL into basket context for any page (e.g. user arriving from email link)
  useEffect(() => {
    if (!router.isReady || !isBasketInitialised) return
    const promoCodeIdFromQuery = router.query.promoCodeId
    const promoCodeId = Array.isArray(promoCodeIdFromQuery) ? promoCodeIdFromQuery[0] : promoCodeIdFromQuery
    if (typeof promoCodeId === 'string' && promoCodeId.length > 0 && promoCodeId !== basketData.promotionCode?.id) {
      setBasketData((prev) => ({
        ...prev,
        promotionCode: { id: promoCodeId, code: '' },
      }))
    }
  }, [router.isReady, router.query.promoCodeId, isBasketInitialised, basketData.promotionCode?.id])

  useEffect(() => {
    if (typeof window !== 'undefined' && isBasketInitialised) {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEYS.BASKET, JSON.stringify(basketData))
      } catch (error) {
        console.error('Error saving basket data to localStorage:', error)
      }
    }
  }, [basketData, isBasketInitialised])

  return (
    <BasketContext.Provider value={{ basketData, setBasketData, isBasketInitialised }}>
      {children}
    </BasketContext.Provider>
  )
}
